import { useTranslation } from "react-i18next";
import { Check, User } from "lucide-react";
import { useOrder } from "@/store/orderStore";
import { useAuth } from "@/auth/AuthProvider";
import { useAuthSheet } from "@/auth/AuthSheetProvider";
import { useTenant } from "@/tenant/TenantProvider";
import { formatPrice } from "@/lib/format";

const PaymentSuccess = ({ onDone, onOpenProfile }) => {
  const { state } = useOrder();
  const { restaurant } = useTenant();
  const { user } = useAuth();
  const { openRegister } = useAuthSheet();
  const { t } = useTranslation();
  const order = state.lastOrder;
  // What the server charged, when we have it — the client total is only a fallback.
  const amount = order ? order.server?.grand ?? order.grand : null;
  const last4 = "4242"; // mock card last 4

  return (
    <div className="px-5 pt-16 pb-12 flex flex-col items-center text-center min-h-screen">
      <div className="w-24 h-24 rounded-full bg-[#22C55E]/10 flex items-center justify-center mb-6">
        <div className="w-16 h-16 rounded-full bg-[#22C55E] flex items-center justify-center">
          <Check size={36} className="text-white" strokeWidth={3} />
        </div>
      </div>

      <h1 data-testid="payment-success-title" className="text-[36px] leading-[1.05] font-black tracking-tight text-neutral-900">
        {t("payment.title")}
      </h1>
      <p className="text-sm text-neutral-500 mt-2">{t("payment.subtitle")}</p>

      <div className="mt-10 w-full bg-white border border-neutral-100 rounded-3xl p-6">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500">
          {t("payment.amountPaid")}
        </div>
        <div data-testid="payment-amount" className="text-4xl font-black text-neutral-900 mt-1">
          {amount !== null ? formatPrice(amount) : "—"}{" "}
          <span className="text-base font-bold text-neutral-500">IDR</span>
        </div>
        <div className="h-px bg-neutral-100 my-4" />
        <div className="flex items-center justify-between">
          <span className="text-sm text-neutral-500"> Apple Pay</span>
          <span className="text-sm font-bold text-neutral-900 font-mono">•••• {last4}</span>
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-sm text-neutral-500">{t("payment.reference")}</span>
          <span className="text-sm font-bold text-neutral-900 font-mono">
            BTR-{(Date.now() % 1000000).toString().padStart(6, "0")}
          </span>
        </div>
      </div>

      {user ? (
        <button
          data-testid="payment-open-profile-btn"
          onClick={onOpenProfile}
          className="mt-6 w-full min-h-14 py-2 px-4 rounded-2xl bg-neutral-900 text-white font-bold text-base tracking-wide active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
        >
          <User size={18} className="shrink-0" /> {t("payment.backToProfile")}
        </button>
      ) : (
        // Still a guest: the order is claimable for as long as this browser keeps
        // its claim token, so offer to save it before that is gone.
        <button
          data-testid="payment-signin-btn"
          onClick={openRegister}
          className="mt-6 w-full min-h-14 py-2 px-4 rounded-2xl bg-neutral-900 text-white font-bold text-base tracking-wide active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
        >
          <User size={18} className="shrink-0" /> {t("payment.saveOrder")}
        </button>
      )}

      <button
        data-testid="payment-done-btn"
        onClick={onDone}
        className={`w-full h-14 rounded-2xl font-bold text-base tracking-wide active:scale-[0.98] transition-transform ${
          user ? "mt-3 bg-white border border-neutral-200 text-neutral-800" : "mt-8 bg-[#E34A27] text-white"
        }`}
      >
        {t("payment.done")}
      </button>

      <p className="text-xs text-neutral-400 mt-6 italic">{t("payment.mockNote")}</p>

      <div className="mt-6 text-[10px] text-neutral-400 text-center">
        {restaurant.name}
        {restaurant.branding?.location ? ` · ${restaurant.branding.location}` : ""}
      </div>
    </div>
  );
};

export default PaymentSuccess;
